import { zodResolver } from '@hookform/resolvers/zod';
import type { SignupRegion } from '@server/signup-answers';
import { useMutation } from '@tanstack/react-query';
import { MailCheck } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Separator } from '@/_shared/components/ui/separator';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { GoogleRegionStep, GoogleSignupButton } from '../components/nativeSignIn/GoogleSignup';
import { RequireNativeSignIn } from '../components/nativeSignIn/RequireNativeSignIn';
import { TextButton, TextLink } from '../components/nativeSignIn/TextLink';
import { SignInErrorCallout } from '../components/SignInErrorCallout';
import { useSignInError } from '../hooks/useSignInError';
import { emailSchema, type EmailValues } from '../lib/authFormSchema';
import type { HostedUiConfig } from '../lib/hosted-ui';
import { sendSignupEmail } from '../lib/rudderstack-auth-client';
import { inviteIdOfReturnTo, isInvitee } from '../lib/signup-invite';

// The same pages the web app's sign-up links to.
const PRIVACY_POLICY_URL = 'https://rudderstack.com/privacy-policy';
const TERMS_OF_SERVICE_URL = 'https://www.rudderstack.com/terms-of-service';
const LEGAL_LINK_CLASSES = 'text-primary underline-offset-4 hover:underline';

/**
 * `/signup`: emails the link that finishes a RudderStack account, or signs up with Google when the Hosted UI is
 * configured. `?invite=1` with `return_to=/invite/<id>` marks a Lookout invitee. `?error=` is the error of a Google
 * sign-up that started here.
 */
export function Signup(): React.ReactElement {
  usePageTitle('Create account');
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');
  // A link with `?invite=1` but no invite is an ordinary sign-up, so it shows no invite text.
  const invite = searchParams.get('invite') === '1' && inviteIdOfReturnTo(returnTo) !== null;

  return (
    <RequireNativeSignIn>
      {(config) => (
        <SignupEmailForm
          apiBaseUrl={config.apiBaseUrl}
          federated={config.federated}
          hostedUi={config.hostedUi}
          invite={invite}
          returnTo={returnTo}
        />
      )}
    </RequireNativeSignIn>
  );
}

function SignupEmailForm({
  apiBaseUrl,
  federated,
  hostedUi,
  invite,
  returnTo,
}: {
  apiBaseUrl: string;
  federated: boolean;
  hostedUi: HostedUiConfig | null;
  invite: boolean;
  returnTo: string | null;
}): React.ReactElement {
  const signInError = useSignInError();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [pickingRegion, setPickingRegion] = useState(false);
  const [region, setRegion] = useState<SignupRegion>('US');
  const backFromGoogle = useRef(false);
  const form = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onTouched',
    defaultValues: { email: '' },
  });
  const inviteId = invite ? inviteIdOfReturnTo(returnTo) : null;
  const send = useMutation({
    mutationFn: async (email: string) =>
      sendSignupEmail(apiBaseUrl, { email, invite: await isInvitee(inviteId, email) }),
  });
  const signInHref = returnTo ? `/login?return_to=${encodeURIComponent(returnTo)}` : '/login';

  if (sentTo) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<MailCheck />}
          variant="success"
          title="Check your email"
          description={
            <>
              We sent a link to <span className="font-medium break-all text-foreground">{sentTo}</span>. Open it to
              finish creating your account.
            </>
          }
        >
          <div className="flex flex-col items-center gap-3">
            {send.error && (
              <Callout variant="error" role="alert" className="w-full text-left">
                {send.error.message}
              </Callout>
            )}
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              loading={send.isPending}
              onClick={() => send.mutate(sentTo, { onSuccess: () => toast.success('We sent the email again.') })}
            >
              Resend email
            </Button>
            <TextButton
              disabled={send.isPending}
              onClick={() => {
                send.reset();
                setSentTo(null);
              }}
            >
              Use a different email
            </TextButton>
          </div>
        </GatewayContent>
      </GatewayShell>
    );
  }

  if (pickingRegion) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent title="Sign up with Google" description="Pick a region, then continue with Google.">
          <GoogleRegionStep
            hostedUi={hostedUi}
            returnTo={returnTo}
            region={region}
            onRegionChange={setRegion}
            onBack={() => {
              backFromGoogle.current = true;
              setPickingRegion(false);
            }}
          />
          <SignupConsent />
          <p className="mt-4 text-xs text-muted-foreground">
            Already have an account? <TextLink to={signInHref}>Sign in</TextLink>
          </p>
        </GatewayContent>
      </GatewayShell>
    );
  }

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      {signInError.message && <SignInErrorCallout message={signInError.message} className="mb-6" />}
      <GatewayContent
        title="Create your account"
        description={
          invite
            ? 'Use your work email to join the workspace, and we’ll send you a link to continue.'
            : 'Use your work email and we’ll send you a link to continue.'
        }
      >
        <Form {...form}>
          <form
            noValidate
            aria-label="Create your account"
            className="flex flex-col gap-4 text-left"
            onSubmitCapture={signInError.dismiss}
            onSubmit={form.handleSubmit((values) =>
              send.mutate(values.email, { onSuccess: () => setSentTo(values.email) }),
            )}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" size="lg" autoComplete="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {send.error && (
              <Callout variant="error" role="alert">
                {send.error.message}
              </Callout>
            )}
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={send.isPending}>
              Continue
            </Button>
          </form>
        </Form>
        {federated && (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Separator className="flex-1" />
              or
              <Separator className="flex-1" />
            </div>
            <GoogleSignupButton
              ref={(node) => {
                // Back from the region step returns the focus to the button that opened it.
                if (node && backFromGoogle.current) {
                  backFromGoogle.current = false;
                  node.focus();
                }
              }}
              hostedUi={hostedUi}
              returnTo={returnTo}
              invite={invite}
              onPickRegion={() => setPickingRegion(true)}
              onStart={signInError.dismiss}
            />
          </div>
        )}
        <SignupConsent />
        <p className="mt-4 text-xs text-muted-foreground">
          Already have an account? <TextLink to={signInHref}>Sign in</TextLink>
        </p>
      </GatewayContent>
    </GatewayShell>
  );
}

/** The web app's sign-up agreement. It covers the email and the Google sign-up. */
function SignupConsent(): React.ReactElement {
  return (
    <p className="mt-4 text-xs text-muted-foreground">
      By signing up, I agree to RudderStack&apos;s{' '}
      <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className={LEGAL_LINK_CLASSES}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer" className={LEGAL_LINK_CLASSES}>
        Terms of Service
      </a>
    </p>
  );
}
